import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VersionSwitcher from "@/components/VersionSwitcher";
import { API } from "@/App";
import {
  Users,
  TrendingUp,
  Sparkles,
  Handshake,
  Compass,
  Trophy,
  Store,
  ShoppingBag,
  Calendar,
  Network,
  GraduationCap,
  Newspaper,
  Award,
  HeartHandshake,
  Bot,
  Globe,
} from "lucide-react";

/**
 * About Us — The BLK Community
 * Positioned as a movement introduction rather than a corporate about page.
 * Route: /about (primary) and /founder (legacy alias, kept for old links).
 */

const ECOSYSTEM_ITEMS = [
  { icon: Store, label: "Business Listings" },
  { icon: ShoppingBag, label: "Marketplace" },
  { icon: Calendar, label: "Events" },
  { icon: Network, label: "Networking Opportunities" },
  { icon: GraduationCap, label: "Educational Resources" },
  { icon: Newspaper, label: "Business News" },
  { icon: Award, label: "Founding Business Program" },
  { icon: HeartHandshake, label: "Community Partnerships" },
  { icon: Bot, label: "Future AI Business Tools" },
  { icon: Globe, label: "Global Business Discovery" },
];

const CORE_VALUES = [
  {
    icon: Users,
    title: "Community First",
    body: "Relationships are the foundation of everything we build.",
  },
  {
    icon: TrendingUp,
    title: "Economic Empowerment",
    body: "Supporting Black-owned businesses strengthens families and communities.",
  },
  {
    icon: Sparkles,
    title: "Excellence",
    body: "We celebrate quality, professionalism, and innovation.",
  },
  {
    icon: Handshake,
    title: "Collaboration",
    body: "Success grows when businesses work together instead of competing alone.",
  },
  {
    icon: Compass,
    title: "Accessibility",
    body: "Every business deserves the opportunity to be discovered.",
  },
  {
    icon: Trophy,
    title: "Legacy",
    body: "We're building something that creates opportunities for generations to come.",
  },
];

export default function FounderPage() {
  // Live stats for the hero strip — businesses, active categories, states covered.
  // Fetched from public endpoints so no auth needed. Falls back to hyphens
  // if the endpoints are unreachable so the strip doesn't flash empty.
  const [stats, setStats] = useState({ businesses: null, categories: null, states: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [community, activeCats, stateBreakdown] = await Promise.all([
          axios.get(`${API}/community/stats`),
          axios.get(`${API}/businesses/categories/active`),
          axios.get(`${API}/businesses/states`),
        ]);
        if (cancelled) return;
        setStats({
          businesses: community.data?.total_businesses ?? null,
          categories: (activeCats.data?.categories || []).length || null,
          states: (stateBreakdown.data?.states || []).length || null,
        });
      } catch {
        // keep the fallback rendering
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fmt = (n) => {
    if (n == null) return "—";
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k+`;
    // Show "X+" for whole numbers so the copy reads as growth, not a ceiling.
    return `${n}+`;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />

      {/* HERO — dark navy, matches LandingPageV4 vibe */}
      <section className="relative overflow-hidden bg-tbc-navy text-white py-20 sm:py-28">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 20%, #F5C518 0px, transparent 40%), radial-gradient(circle at 80% 70%, #F5C518 0px, transparent 45%)",
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 border border-tbc-gold/40 text-tbc-gold rounded-full text-xs uppercase tracking-[0.22em] mb-6">
            About The BLK Community
          </span>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight"
            data-testid="about-page-heading"
          >
            The Home for <span className="text-tbc-gold">Black Business</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed font-body">
            The BLK Community (TBC) was created with one simple belief:
          </p>
          <p className="mt-3 font-heading text-tbc-gold text-xl sm:text-2xl italic">
            When Black businesses thrive, our communities thrive.
          </p>

          {/* Live stat strip — sits at the top of the About page so first-time
              visitors immediately see the platform's momentum. Numbers pull
              from /api/community/stats + /api/businesses/categories/active +
              /api/businesses/states so they stay in sync with the DB. */}
          <div
            className="mt-10 grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto"
            data-testid="about-stat-strip"
          >
            <div className="border-t border-tbc-gold/30 pt-4">
              <p className="font-heading text-3xl sm:text-4xl text-tbc-gold" data-testid="stat-businesses">
                {fmt(stats.businesses)}
              </p>
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/60 mt-1">
                Businesses
              </p>
            </div>
            <div className="border-t border-tbc-gold/30 pt-4">
              <p className="font-heading text-3xl sm:text-4xl text-tbc-gold" data-testid="stat-categories">
                {fmt(stats.categories)}
              </p>
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/60 mt-1">
                Categories
              </p>
            </div>
            <div className="border-t border-tbc-gold/30 pt-4">
              <p className="font-heading text-3xl sm:text-4xl text-tbc-gold" data-testid="stat-states">
                {stats.states == null ? "—" : `${stats.states}+`}
              </p>
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/60 mt-1">
                {stats.states && stats.states < 25 ? "States" : "Nationwide"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OPENING NARRATIVE */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5 text-gray-700 font-body text-base sm:text-lg leading-relaxed">
          <p>
            We&rsquo;re building more than a business directory&mdash;we&rsquo;re
            creating a global platform where Black entrepreneurs, professionals,
            creators, nonprofits, and consumers can discover one another, build
            meaningful relationships, and grow together.
          </p>
          <p>
            Whether you&rsquo;re searching for a local restaurant, a trusted
            realtor, a talented photographer, a skilled attorney, or your next
            favorite Black-owned brand, The BLK Community exists to make those
            connections easier than ever.
          </p>
          <p>
            From local neighborhoods to cities across the globe, our mission is
            to make supporting Black businesses simple, accessible, and
            impactful.
          </p>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="py-16 sm:py-20 bg-tbc-cream grain-texture">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <div
            className="bg-white rounded-2xl p-8 sm:p-10 border border-tbc-navy/5 shadow-sm"
            data-testid="mission-card"
          >
            <span className="inline-block text-[10px] uppercase tracking-[0.24em] text-tbc-gold font-medium mb-3">
              Our Mission
            </span>
            <p className="text-gray-800 font-body leading-relaxed">
              To connect, promote, and empower Black-owned businesses worldwide
              by providing a trusted platform where communities can discover
              services, shop intentionally, build partnerships, and create
              generational wealth together.
            </p>
          </div>
          <div
            className="bg-tbc-navy text-white rounded-2xl p-8 sm:p-10 shadow-sm"
            data-testid="vision-card"
          >
            <span className="inline-block text-[10px] uppercase tracking-[0.24em] text-tbc-gold font-medium mb-3">
              Our Vision
            </span>
            <p className="font-body leading-relaxed text-white/90">
              To become the global home for Black business&mdash;a place where
              every entrepreneur has an opportunity to be discovered and every
              consumer can intentionally support businesses that strengthen our
              communities.
            </p>
          </div>
        </div>
      </section>

      {/* MORE THAN A DIRECTORY — ecosystem grid */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl text-tbc-navy">
              More Than a Directory
            </h2>
            <p className="mt-3 text-gray-600 font-body leading-relaxed">
              The BLK Community is growing into an ecosystem designed to help
              businesses succeed. Our platform brings together:
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ecosystem-grid">
            {ECOSYSTEM_ITEMS.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="group border border-gray-100 rounded-xl p-4 hover:border-tbc-gold hover:shadow-md transition-all bg-white"
              >
                <div className="w-9 h-9 rounded-lg bg-tbc-navy/5 flex items-center justify-center mb-3 group-hover:bg-tbc-gold/15 transition-colors">
                  <Icon className="w-4 h-4 text-tbc-navy group-hover:text-tbc-gold transition-colors" />
                </div>
                <p className="text-sm font-heading text-tbc-navy leading-tight">
                  {label}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-gray-600 font-body italic text-center max-w-2xl mx-auto">
            Whether you&rsquo;re launching your first business or scaling your
            hundredth location, TBC is designed to grow with you.
          </p>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="py-16 sm:py-20 bg-tbc-cream grain-texture">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 bg-tbc-gold/15 text-tbc-navy rounded-full text-xs uppercase tracking-[0.18em] font-medium mb-4">
              Meet Our Founder
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl text-tbc-navy">
              Matt Walker
            </h2>
            <p className="text-sm text-gray-500 font-body mt-1">
              Founder &amp; CEO, The BLK Community
            </p>
          </div>

          <div className="flex flex-col items-center mb-10">
            <div className="relative">
              <div className="absolute -inset-2 bg-tbc-gold/20 rounded-full transform -rotate-3"></div>
              <img
                src="/founder.jpg"
                alt="Matt Walker, Founder of The BLK Community"
                className="relative w-40 h-40 sm:w-48 sm:h-48 rounded-full object-cover shadow-lg border-4 border-white"
                data-testid="founder-portrait"
              />
            </div>
          </div>

          <div className="space-y-5 max-w-2xl mx-auto text-gray-700 font-body text-base sm:text-lg leading-relaxed">
            <p>
              Known by many as <strong>King Midas</strong>, Matt Walker has spent
              more than two decades doing what he loves most&mdash;connecting
              people with opportunities.
            </p>
            <p>
              As an entrepreneur, real estate professional since 2017, business
              owner, husband, father of four, and founder of The Midastouch
              Group, LLC, Matt has always believed that relationships are one of
              the most valuable forms of wealth.
            </p>
            <p>
              Throughout his career, he&rsquo;s helped individuals buy, sell,
              rent, and invest in real estate while building businesses centered
              around service, trust, and community.
            </p>

            <p className="font-heading text-tbc-gold text-xl text-center py-2">
              The BLK Community was born from a simple observation:
            </p>
            <p className="font-heading text-tbc-navy text-xl text-center">
              Finding Black-owned businesses shouldn&rsquo;t be difficult.
            </p>

            <p>
              Matt envisioned one place where entrepreneurs could gain
              visibility, consumers could intentionally support Black
              businesses, and communities everywhere could build stronger
              economic connections.
            </p>
            <p>That vision became The BLK Community.</p>
            <p>
              Today, Matt continues working to create a platform that
              doesn&rsquo;t simply list businesses&mdash;it helps businesses
              grow.
            </p>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl text-tbc-navy">
              Our Core Values
            </h2>
            <p className="mt-3 text-gray-600 font-body">
              The principles that guide every decision we make.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="core-values-grid">
            {CORE_VALUES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-2xl border border-tbc-navy/10 p-6 bg-tbc-cream/40 hover:bg-white hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-tbc-navy text-tbc-gold flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-lg text-tbc-navy">{title}</h3>
                <p className="mt-2 text-sm text-gray-600 font-body leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOIN THE MOVEMENT */}
      <section className="py-16 sm:py-20 bg-tbc-cream grain-texture">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-tbc-navy">
            Join the Movement
          </h2>
          <div className="space-y-4 mt-6 text-gray-700 font-body text-base sm:text-lg leading-relaxed">
            <p>
              Whether you&rsquo;re a business owner looking to grow, a shopper
              looking to support Black-owned brands, or someone who believes in
              strengthening communities through commerce, there&rsquo;s a place
              for you here.
            </p>
            <p className="font-heading text-tbc-navy text-xl">
              The BLK Community isn&rsquo;t just where you find Black
              businesses.
            </p>
            <p className="font-heading text-tbc-gold text-xl italic">
              It&rsquo;s where Black business comes together.
            </p>
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-16 sm:py-20 bg-tbc-navy text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl">Ready to Join?</h2>
          <p className="mt-4 text-white/70 font-body leading-relaxed max-w-2xl mx-auto">
            Become part of a growing global community connecting businesses,
            entrepreneurs, and consumers through commerce, collaboration, and
            opportunity.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8" data-testid="about-cta-buttons">
            <Link to="/directory">
              <Button
                variant="outline"
                className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-tbc-navy rounded-full px-8 py-6 font-medium text-base w-full sm:w-auto"
                data-testid="about-cta-explore"
              >
                Explore Businesses
              </Button>
            </Link>
            <Link to="/join">
              <Button
                className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8 py-6 font-medium text-base w-full sm:w-auto"
                data-testid="about-cta-add"
              >
                Add Your Business
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                variant="outline"
                className="border-2 border-tbc-gold text-tbc-gold bg-transparent hover:bg-tbc-gold hover:text-tbc-navy rounded-full px-8 py-6 font-medium text-base w-full sm:w-auto"
                data-testid="about-cta-founding"
              >
                Become a Founding Business
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <VersionSwitcher />
    </div>
  );
}
